import { InputFormFieldProps } from '@crm-framework/form-fields';
import { IBusinessUnitRights } from '@crm-framework/auth';

import {
  IBusinessUnitModel,
  IDictionaries,
  IDictionaryWithOrder,
  IEnvironmentModel,
  ILanguageModel,
} from '@crm-package/dictionaries';
import { ILeadResponseModel } from '@crm-lead/base';

import {
  PROMO_TYPE,
  visible,
  leadRetentionFilter,
  MAX_USAGE,
  DEPOSIT_COUNT_FROM,
  DEPOSIT_COUNT_TO,
  TOTAL_BONUS_AMOUNT,
  TOTAL_DEPOSIT_TO,
  TOTAL_DEPOSIT_FROM,
} from './consts';

export enum locations {
  LOCATION_PROMO_DASHBOARD = 'LOCATION_PROMO_DASHBOARD',
  LOCATION_LEAD_DASHBOARD = 'LOCATION_LEAD_DASHBOARD',
}

export interface IPromoPreviewProps {
  id: string;
  location: locations;
  lead?: ILeadResponseModel;
}

export interface IPromoDrawerProps extends IPromoPreviewProps {
  onClose: () => void;
}

export interface IRulesProps {
  disabled: boolean;
}

export interface IDescriptionProps {
  disabled: boolean;
}
export interface IRulesInputProps extends InputFormFieldProps {
  rulesLength: number;
  index?: number;
}

export interface ITabFieldsProps {
  index: number;
  isRequired?: boolean;
  disabled: boolean;
  languageName?: string;
}

export interface IDictionaryItem {
  id: number | string;
  name: string;
  disabled?: boolean;
}

export interface IPromoFormActionsProps {
  id: string;
  disabled: boolean;
  leadId?: number;
}

export interface ILimitationProps {
  isNew: boolean;
  disabled: boolean;
}

export interface IBonusConditions {
  amountFrom?: string | number;
  amountTo?: string | number;
  value?: string | number;
}

export interface IBonusConditionsErrors {
  amountFrom: string | null;
  amountTo: string | null;
  value: string | null;
}
export interface IText {
  languageName: string;
  languageId: number;
  specialOfferId?: number;
  description?: string;
  terms?: string;
  emailBody?: string;
  emailSubject?: string;
  emailFrom?: string;
}

export interface IDescriptionTabProps {
  texts: IText[];
  disabled: boolean;
}

export interface ITextError {
  description?: string | null;
  terms?: string | null;
  emailBody?: string | null;
  emailSubject?: string | null;
  emailFrom?: string | null;
}

type businessUnit = number;

export interface IPromoModel extends ILimitationModel {
  businessUnits: businessUnit[];
  comment: string;
  guid: string;
  id: number;
  leadRetentionFilter: string;
  name: string;
  rule: {
    bonusConditions: IBonusConditions[];
    depositCurrency?: string;
    devaluationCurrency?: string;
  };
  status: string;
  texts: IText[];
  type:
    | typeof PROMO_TYPE.DEPOSIT_BONUS
    | typeof PROMO_TYPE.DEVALUATION_BONUS
    | typeof PROMO_TYPE.FIXED_BONUS;
  usage: number;
  validFrom: Date;
  validTo: Date;
  visible: boolean;
  transactionSubType?: number;
}

export interface ILimitationModel {
  [TOTAL_DEPOSIT_FROM]?: number | null;
  [TOTAL_DEPOSIT_TO]?: number | null;
  [DEPOSIT_COUNT_FROM]?: number | null;
  [DEPOSIT_COUNT_TO]?: number | null;
  [TOTAL_BONUS_AMOUNT]?: number | null;
  [MAX_USAGE]?: number | null;
}

export interface IPromoErrors extends ILimitationModel {
  comment?: string | null;
  guid?: string | null;
  businessUnits?: string | null;
  type?: string | null;
  name?: string | null;
  rule?: {
    bonusConditions: IBonusConditionsErrors[];
    depositCurrency?: string | null;
    devaluationCurrency?: string | null;
  };
  leadRetentionFilter?: string | null;
  status?: string;
  texts: ITextError[];
  usage?: number;
  validFrom?: string;
  validTo?: string;
  visible?: boolean;
  transactionSubType?: number;
}

export interface IPromoType {
  alias: string;
  id: number;
  name: string;
  order: number;
}

export interface IMyAccountUrl {
  environmentId: number;
  value: string;
}

export type DictionaryPromise =
  | Promise<IMyAccountUrl[]>
  | Promise<ILanguageModel[]>
  | Promise<IEnvironmentModel[]>;

export interface IBrandLinkProps {
  location: locations;
  id: string;
  lead?: any; // TODO replace to ILead
}

export interface IGetBUByRights {
  userBURight: string;
  rights: string[];
  businessUnitRights: IBusinessUnitRights;
  businessUnits: IBusinessUnitModel[];
}

export interface ICheckPromoBusinessUnitsByPromoEditRight {
  promoBusinessUnits: businessUnit[];
  rights: string[];
  businessUnitRights: IBusinessUnitRights;
  businessUnits: IBusinessUnitModel[];
}

export interface IPromoLinkProps {
  id: string;
}

export interface ISetDictionaries {
  location: locations;
}

export interface IPromoModalProps {
  path: string;
  languageName: string;
}

export interface ITypeModel extends IDictionaryWithOrder {
  alias: string;
}

export interface IMappedTypeModel {
  id: string;
  name: string;
}

export interface IBusinessUnitsByEnvironment {
  label: string;
  options: IBusinessUnitModel[];
}

export interface IPromoDictionaries extends IDictionaries {
  visible: typeof visible;
  myAccountUrls: IMyAccountUrl[];
  leadRetentionFilter: typeof leadRetentionFilter;
  type: ITypeModel[];
  mappedTypes: IMappedTypeModel[];
  businessUnitsByEnviroments: IBusinessUnitsByEnvironment[];
}

export type DictionariesForSetType = Partial<
  Pick<
    IPromoDictionaries,
    | 'leadRetentionFilter'
    | 'visible'
    | 'businessUnit'
    | 'businessUnitsByEnviroments'
    | 'mappedTypes'
  >
>;
